import {
  CULTURE_ROUTE_PARAM,
  DynamicFlowBaseUrlEnum,
  FlowBaseUrlEnum,
} from '@constants/app.constants';
import { FlowPathEnum } from '@models/flow-wrapper.model';
import { getRouterSelectors } from '@ngrx/router-store';
import { createSelector } from '@ngrx/store';
import { selectCultures } from './resource.selector';

export const {
  selectCurrentRoute, // select the current route
  selectFragment, // select the current route fragment
  selectQueryParams, // select the current route query params
  selectQueryParam, // factory function to select a query param
  selectRouteParams, // select the current route params
  selectRouteParam, // factory function to select a route param
  selectRouteData, // select the current route data
  selectRouteDataParam, // factory function to select a route data param
  selectUrl, // select the current url
  selectTitle, // select the title if available
} = getRouterSelectors();

export const selectIsPayPath = createSelector(
  selectUrl,
  (url) => !!url?.includes(`/${FlowBaseUrlEnum.PAY}/`),
);

export const selectIsModifyPath = createSelector(
  selectUrl,
  (url) => !!url?.includes(`/${FlowBaseUrlEnum.MODIFY}/`),
);

export const selectIsCancelPath = createSelector(
  selectUrl,
  (url) => !!url?.includes(`/${FlowBaseUrlEnum.CANCEL}/`),
);

export const selectIsBookPath = createSelector(
  selectUrl,
  (url) => !!url?.includes(`/${FlowBaseUrlEnum.BOOK}/`),
);

export const selectIsBookConfirmationPath = createSelector(
  selectUrl,
  (url) => !!url?.includes(`/${FlowBaseUrlEnum.BOOK}/confirmation`),
);

export const selectIsSupportPage = createSelector(
  selectUrl,
  (url) => !!url?.includes(`/${DynamicFlowBaseUrlEnum.SUPPORT}/`),
);

export const selectIsUpgradePath = createSelector(
  selectUrl,
  (url) => !!url?.includes(`/${FlowBaseUrlEnum.UPGRADE}/`),
);

export const selectIsCheckInPath = createSelector(
  selectUrl,
  (url) => !!url?.includes(`/${FlowBaseUrlEnum.CHECK_IN}/`),
);

export const selectIsTripsPath = createSelector(
  selectUrl,
  (url) => !!url?.includes(`/${FlowBaseUrlEnum.TRIPS}/`),
);

export const selectAppFlowPathName = createSelector(selectUrl, (url) => {
  const urlSegments = url?.split('/') ?? [];
  // Ex: ['', 'en-US', 'book', 'payment']
  const flow = Object.values(FlowBaseUrlEnum).find((f) => urlSegments[2] === f);
  // Ex: ['', 'en-US', 'corporate', 'about', 'our-values']
  const dynamicFlow = Object.values(DynamicFlowBaseUrlEnum).find(
    (f) => urlSegments[2] === f,
  );
  return flow ?? dynamicFlow ?? null;
});

export const selectUrlNoQueryString = createSelector(
  selectUrl,
  (url) => url?.split('?')[0],
);

export const selectIsFlightsPage = createSelector(
  selectUrlNoQueryString,
  (url) => !!url?.endsWith(`/${FlowPathEnum.FLIGHTS}`),
);

export const selectIsPaymentsPage = createSelector(
  selectUrlNoQueryString,
  (url) => !!url?.endsWith(`/${FlowPathEnum.PAYMENT}`) || !!url?.endsWith(`/${FlowPathEnum.REVIEW}`),
);

export const selectIsExtrasPage = createSelector(
  selectUrlNoQueryString,
  (url) => !!url?.endsWith(`/${FlowPathEnum.EXTRAS}`),
);

export const selectIsUpliftPage = createSelector(
  selectUrlNoQueryString,
  (url) => !!url?.endsWith(`/${FlowBaseUrlEnum.UPLIFT}`),
);

export const selectIsFlowPage = createSelector(selectUrl, (url) =>
  Object.values(FlowBaseUrlEnum)
    .filter((v) => v !== FlowBaseUrlEnum.TRIPS && v !== FlowBaseUrlEnum.ACCOUNT)
    .some((v) => url?.includes('/' + v + '/')),
);

export const selectIsFlowOrTripsPage = createSelector(selectUrl, (url) =>
  Object.values(FlowBaseUrlEnum)
    .filter((v) => v !== FlowBaseUrlEnum.ACCOUNT)
    .some((v) => url?.includes('/' + v + '/')),
);

export const selectIsMulticityPage = createSelector(
  selectUrlNoQueryString,
  (url) => !!url?.endsWith(`/${FlowBaseUrlEnum.MULTICITY}`),
);

export const selectIsHomePage = createSelector(
  selectUrlNoQueryString,
  selectRouteParam(CULTURE_ROUTE_PARAM),
  (url, cultureCode) => {
    const urlWithoutQueryString = url?.split('?')[0];

    return (
      urlWithoutQueryString?.endsWith(`/${cultureCode}`) ||
      url?.endsWith(`/${FlowPathEnum.HOME}`)
    );
  },
);

export const selectIsCultureValidAndEnabled = createSelector(
  selectRouteParam(CULTURE_ROUTE_PARAM),
  selectCultures,
  (cultureCode, cultures) => {
    return (
      cultures?.some((culture) => culture.cultureCode === cultureCode) ?? false
    );
  },
);

export const selectIsSessionFlow = createSelector(
  selectIsPayPath,
  selectIsModifyPath,
  selectIsCancelPath,
  selectIsBookPath,
  selectIsUpgradePath,
  selectIsCheckInPath,
  selectIsTripsPath,
  selectIsFlightsPage,
  (
    isPayPath,
    isModifyPath,
    isCancelPath,
    isBookPath,
    isUpgradePath,
    isCheckInPath,
    isTripsPath,
  ) => {
    return isPayPath || isModifyPath || isCancelPath || isBookPath || isUpgradePath || isCheckInPath || isTripsPath;
  }
);

